import type { ComponentRenderProps } from "@json-render/react";
import { z } from "zod";

import {
  parseReportSpacing,
  parseReportTextStyle,
  REPORT_HIGHLIGHT_MAX_ACCENT_WIDTH_PX,
  REPORT_SPACING_DESCRIPTION,
  REPORT_TEXT_STYLE_DESCRIPTION,
  reportHighlightPresentationSchema,
} from "@/components/ui/report/reportPresentation";

import { ReportHighlight } from "./ReportHighlight";

const EXAMPLE_CONTENT =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.";

const reportHighlightAccentWidthPxSchema =
  reportHighlightPresentationSchema.shape.accentWidthPx;

/** Layout Studio catalog definition for ReportHighlight. */
export const ReportHighlightSpec = {
  props: z.object({
    ...reportHighlightPresentationSchema.shape,
    title: z.string().nullable().optional(),
    content: z.string(),
  }),
  description: `Report highlight / executive summary block: left accent bar, tinted background, title and content. Title defaults to Today at a Glance when omitted; choose a descriptive heading for the brief. Use bracketed placeholders for missing findings, not realistic news. Content supports Markdown paragraphs, emphasis and lists; match the requested summary format. Omit borderColor and backgroundColor unless the user asks for a one-off color; defaults use theme primary for accent/title and a light primary tint for the background. Optional presentation (omit for the default look): accentWidthPx sets the left accent bar width (integer 0-${REPORT_HIGHLIGHT_MAX_ACCENT_WIDTH_PX}, default 4; 0 hides it). titleStyle and contentStyle set title and content typography (${REPORT_TEXT_STYLE_DESCRIPTION}). spacing sets box padding and the title-to-content gap (${REPORT_SPACING_DESCRIPTION}).`,
  example: {
    title: "Today at a Glance",
    content: EXAMPLE_CONTENT,
  },
} as const;

type ReportHighlightPropsFromSpec = {
  title?: string | null;
  content: string;
  borderColor?: string | null;
  backgroundColor?: string | null;
  accentWidthPx?: unknown;
  titleStyle?: unknown;
  contentStyle?: unknown;
  spacing?: unknown;
};

/** Adapter for json-render defineRegistry: renders ReportHighlight from catalog props. */
export const ReportHighlightFromProps = ({ element }: ComponentRenderProps) => {
  const {
    title,
    content,
    borderColor,
    backgroundColor,
    accentWidthPx,
    titleStyle,
    contentStyle,
    spacing,
  } = element.props as ReportHighlightPropsFromSpec;
  const accentWidthPxResult =
    reportHighlightAccentWidthPxSchema.safeParse(accentWidthPx);

  return (
    <ReportHighlight
      title={title ?? undefined}
      content={content}
      borderColor={borderColor ?? undefined}
      backgroundColor={backgroundColor ?? undefined}
      accentWidthPx={
        accentWidthPxResult.success ? accentWidthPxResult.data : undefined
      }
      titleStyle={parseReportTextStyle(titleStyle)}
      contentStyle={parseReportTextStyle(contentStyle)}
      spacing={parseReportSpacing(spacing)}
    />
  );
};
